// React and hooks
import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@material-ui/core';

// Material-UI components
import { CardContent, CardHeader, Card } from '@material-ui/core';

// Local components
import SummaryParagraphHeader from '../SummaryParagraphHeader';
import { ReviewerSelector } from '../SummaryCommon/ReviewerSelector';
import BiradsCategoryLesions from './DiagnosticLesions';
import DiagnosticTimeline from './DiagnosticTimeline';
import { DiagnosticsCardActions } from './DiagnosticsCardActions';

// Custom hooks
import { useDiagnosticsData } from './useDiagnosticsData';
import { useDiagnosticsHandlers } from './useDiagnosticsHandlers';
import { useDiagnosticsEffects } from './useDiagnosticsEffects';

// Styles
import { useStyles } from './DiagnosticSummary.style';

export default function DiagnosticsSummary() {
  // Hooks
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const smallScreen = useMediaQuery('(max-width: 1400px)');

  const dispatch = useDispatch();

  // Store state
  const { examinationListDrawer, zoomedProjection } = useSelector((store) => store.gui);
  const { premium_features } = useSelector((state) => state.global_settings);
  const { diagnostics_microcalc_module, diagnostics_opacities_module, data_annotation } = premium_features;
  const patientHistory = useSelector((state) => state.patientHistory);

  // Custom hooks
  const diagnosticsData = useDiagnosticsData(t, theme, zoomedProjection);
  const handlers = useDiagnosticsHandlers(t);
  const effects = useDiagnosticsEffects(diagnosticsData.results);

  // Derived state
  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';
  const isMicrocalcConfirmed =
    diagnosticsData.applied_microcalc === 'bbox' && diagnosticsData.confirmed_microcalc;
  const isMicrocalcRelabeled =
    diagnosticsData.confirmed_microcalc && diagnosticsData.applied_microcalc !== 'bbox';
  const isOpacityConfirmed =
    diagnosticsData.applied_opacities === 'bbox' && diagnosticsData.confirmed_opacities;
  const isOpacityRelabeled =
    diagnosticsData.confirmed_opacities && diagnosticsData.applied_opacities !== 'bbox';

  const smallScreenWithDrawer = smallScreen || examinationListDrawer;

  // Memoized handlers with proper dependencies
  const handleChipMouseEnter = useMemo(
    () => (module, exist, isShown, classLabel) => {
      handlers.handleChipMouseEnter(module, exist, isShown, classLabel, effects.lockedClasses);
    },
    [handlers.handleChipMouseEnter, effects.lockedClasses]
  );

  const handleChipMouseLeave = useMemo(
    () => (module, classLabel) => {
      handlers.handleChipMouseLeave(module, classLabel, effects.lockedClasses);
    },
    [handlers.handleChipMouseLeave, effects.lockedClasses]
  );

  const handleChipClick = useMemo(
    () => (module, classLabel, isShown, overlaysExist) => {
      handlers.handleChipClick(
        module,
        classLabel,
        isShown,
        overlaysExist,
        effects.lockedClasses,
        effects.setLockedClasses
      );
    },
    [handlers.handleChipClick, effects.lockedClasses, effects.setLockedClasses]
  );

  // Fetch patient history
  return (
    <Card className={classes.card_main_container}>
      <CardHeader
        style={{
          padding: '0',
          height: '38px',
          backgroundColor: theme.palette.texts.generalTitleBackgrounds,
        }}
        title={
          <div className={classes.diagnostic_header_container}>
            <SummaryParagraphHeader
              header={t('b_box.b_diagnose')}
              onClick={() => annotatorRole && handlers.handleTitleClick()}
              tootlipTitle={t('ResultView.view_more_diagnose')}
            />

            <ReviewerSelector
              type='diagnostics'
              shownContent={diagnosticsData.microcalcShown || diagnosticsData.opacitiesShown}
              appliedContent={diagnosticsData.applied_microcalc || diagnosticsData.applied_opacities}
              isConfirmed={isMicrocalcConfirmed || isOpacityConfirmed}
              isRelabeled={isMicrocalcRelabeled || isOpacityRelabeled}
              classes={classes}
              onPopperClose={() => {
                effects.setDiagnosticsPopperOpen(false);
              }}
            />
          </div>
        }
      />
      <CardContent style={{ padding: '0' }} className={classes.diagnostic_table_container}>
        {/* BI-RADS Category Lesions */}
        {(diagnostics_opacities_module || diagnostics_microcalc_module) && (
          <BiradsCategoryLesions
            otherLesions={diagnosticsData?.otherLesions}
            onMouseEnter={handleChipMouseEnter}
            onMouseLeave={handleChipMouseLeave}
            onClick={handleChipClick}
            mergedLesionsByCategory={diagnosticsData.mergedLesionsByCategory}
            emptyFindings={diagnosticsData.microcalcEmpty && diagnosticsData.opacitiesEmpty}
            isLoading={diagnosticsData.isDataLoading}
          />
        )}

        {/* Patient History / Timeline */}
        <DiagnosticTimeline
          data={patientHistory}
          onMouseEnter={handleChipMouseEnter}
          onMouseLeave={handleChipMouseLeave}
          onClick={handleChipClick}
          smallScreen={smallScreenWithDrawer}
        />
      </CardContent>
      <DiagnosticsCardActions
        diagnostics_microcalc_module={diagnostics_microcalc_module}
        diagnostics_opacities_module={diagnostics_opacities_module}
        annotatorRole={annotatorRole}
        handleEditButtonClick={handlers.handleEditButtonClick}
        applied_microcalc={diagnosticsData.applied_microcalc}
        microcalcShown={diagnosticsData.microcalcShown}
        isMicrocalcConfirmed={isMicrocalcConfirmed}
        isMicrocalcRelabeled={isMicrocalcRelabeled}
        onMicrocalcConfirmClick={handlers.onMicrocalcConfirmClick}
        applied_opacities={diagnosticsData.applied_opacities}
        opacitiesShown={diagnosticsData.opacitiesShown}
        isOpacityConfirmed={isOpacityConfirmed}
        isOpacityRelabeled={isOpacityRelabeled}
        onOpacitiesConfirmClick={handlers.onOpacitiesConfirmClick}
        results={
          diagnosticsData?.results?.evaluations?.microcalc || diagnosticsData?.results?.evaluations?.opacities
        }
        theme={theme}
        t={t}
      />
    </Card>
  );
}
